import awsconfig from '@/amplifyconfiguration.json';
import { getPatient } from '@/graphql/queries';
import { dateUtils } from '@/utils/dateUtils';
import { formatUtils } from '@/utils/formatUtils';
import { Box, Button, Link, styled, Typography } from '@mui/material';
import { Amplify } from 'aws-amplify';
import { generateClient } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { reserveAPIClient } from '@/client/axios';

Amplify.configure(awsconfig);
const client = generateClient();

type DischargeFileType = {
  fileName: string;
  filePath: string;
};

type GetReservationListType = {
  date: string;
  facilityName: string;
  outpatientId: string;
  outpatientName: string;
  startTime: string;
  receptionKbn: string;
  paymentFlag: string;
  reserveDischargeFileList: DischargeFileType[];
};

/**
 * 診療履歴
 * @returns 診療履歴
 */
export function MedicalHistory() {
  const [historyList, setHistoryList] = useState<GetReservationListType[]>([]);
  const [showMore, setShowMore] = useState<boolean>(false);

  const fetchHistory = async () => {
    const session = await fetchAuthSession();
    const cognitoid = session.userSub;
    if (!cognitoid) {
      return;
    }

    await client
      .graphql({
        query: getPatient,
        variables: { id: cognitoid },
        authMode: 'userPool'
      })
      .then(async () => {
        const res = await reserveAPIClient.get<{ id: string }, AxiosResponse<GetReservationListType[]>>(
          `api/reservation/getreservationlist/${cognitoid}`
        );
        const filterHistoryList = res.data.filter((history) => {
          return history.receptionKbn !== '0' && dateUtils.diffNow(history.date) <= 0;
        });
        setHistoryList(filterHistoryList);
      });
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  return (
    <>
      {historyList
        .filter((_, index) => (showMore ? showMore : index < 3))
        .map((history, index) => (
          <Outpatient
            key={`consultation-${index}`}
            date={history.date}
            startTime={history.startTime}
            facilityName={history.facilityName}
            outpatient={history.outpatientName}
            reserveDischargeFileList={history.reserveDischargeFileList}
          />
        ))}

      {historyList.length > 3 && !showMore && (
        <Button
          className="text-primary"
          variant="outlined"
          sx={{
            width: '100%',
            height: '36px',
            textAlign: 'center',
            borderColor: 'grey.300'
          }}
          onClick={() => setShowMore(true)}
        >
          もっと見る
        </Button>
      )}
    </>
  );
}

const SmallTypography = styled(Typography)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 'bold'
}));
const MiddleTypography = styled(Typography)(({ theme }) => ({
  fontSize: '1rem'
}));

interface OutpatientProps {
  date: string;
  startTime: string;
  facilityName: string;
  outpatient: string;
  reserveDischargeFileList?: DischargeFileType[];
}
const Outpatient = ({ date, startTime, facilityName, outpatient, reserveDischargeFileList = [] }: OutpatientProps) => {
  return (
    <Box border={1} borderColor="grey.300" borderRadius={2} p={2} mb={1} sx={{ backgroundColor: 'white' }}>
      <SmallTypography>
        {formatUtils.formatDate(date)}
        &nbsp;
        {formatUtils.formatTime(startTime)}
      </SmallTypography>
      <Typography sx={{ mt: 2, fontSize: '12px' }}>{`${facilityName}`}</Typography>
      <Box sx={{ width: '100%', display: 'inline-flex', borderColor: 'grey.300' }}>
        <MiddleTypography sx={{ mr: 2, fontWeight: 'bold' }}>{`${outpatient}`}</MiddleTypography>
        <MiddleTypography sx={{ mb: 2, whiteSpace: 'nowrap' }}>{`診療時間`}</MiddleTypography>
        <MiddleTypography sx={{ mr: 2, mb: 2, whiteSpace: 'nowrap' }}>{'60分'}</MiddleTypography>
      </Box>

      {reserveDischargeFileList.length > 0 && (
        <>
          <Box sx={{ width: '100%', display: 'inline-flex', borderTop: '1px solid', borderColor: 'grey.300' }}>
            <SmallTypography sx={{ mt: 2, mb: 1 }}>配布物</SmallTypography>
          </Box>
          {reserveDischargeFileList.map((dischargeFile) => (
            <Link sx={{ display: 'block', fontSize: '1rem', mt: 1, fontWeight: 'bold' }} href={dischargeFile.filePath}>
              {dischargeFile.fileName}
            </Link>
          ))}
        </>
      )}
    </Box>
  );
};
